import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import { MRT_Row, MRT_ColumnDef } from "material-react-table"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { COMPANY_ADMIN_ROUTE, COMPANY_ADMIN_ORGANIZATION_USERS_CREATE_ROUTE } from "../../../../../../constants/routes"
import React, { useCallback, useMemo, useState } from "react"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { Button } from "../../../../../office/admin/components/Button"
import { MenuItem } from "@mui/material"
import { useNavigate } from "react-router"
import { UserDetailsDialog } from "../../../../../office/admin/components/UserDetails/UserDetailsDialog"
import { CompaniesUsersResponse, CompanyAdminUsersQuery, fetchCompaniesUsers } from "../usersApi"
import { SelectUserGroupDropdown } from "../components/SelectUserGroupDropdown"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"
import { CompaniesUsersItem } from "../../../../../users/userTypes"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"

type Props = Readonly<{}>

export enum UserGroup {
    ALL = 0,
}

function transData(data: CompaniesUsersResponse) {
    return data.users
}

export function Users({}: Props) {
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const { companyId } = useCurrentCompany()

    const [userGroupId, setUserGroupId] = useState<UserGroup>(UserGroup.ALL)
    const [showDetailsModal, setShowDetailsModal] = useState(false)
    const [user, setUser] = useState<CompaniesUsersItem | null>()

    const companyUsers = useApiQuery(fetchCompaniesUsers, {
        queryName: CompanyAdminUsersQuery.USERS_FOR_COMPANY,
        dependencies: {
            userGroupId,
            companyId,
        },
        select: transData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(companyUsers)

    const columns = useMemo<MRT_ColumnDef<CompaniesUsersItem>[]>(
        () => [
            columnFactory.shortNameColumn({
                header: l10n.getString("company-organization-users-table-header-user-group"),
                accessorKey: "userGroup.name",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("company-organization-users-table-header-employee-id"),
                accessorKey: "employeeId",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("company-organization-users-table-header-name"),
                accessorKey: "displayName",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("company-organization-users-table-header-email"),
                accessorKey: "email",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("company-organization-users-table-header-status"),
                    accessorKey: "isActive",
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<CompaniesUsersItem>; closeMenu: () => void }) => [
            <MenuItem
                key={`details-${row.original.id}`}
                onClick={() => {
                    setUser(row.original)
                    setShowDetailsModal(true)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="company-organization-users-table-button-details">
                    <span>Details</span>
                </LocalizedStrict>
            </MenuItem>,
            <MenuItem
                key={`edit-${row.original.id}`}
                onClick={() => {
                    const navState = { user: row.original }
                    navigate(COMPANY_ADMIN_ORGANIZATION_USERS_CREATE_ROUTE, { state: navState })
                    closeMenu()
                }}
            >
                <LocalizedStrict id="company-organization-users-table-button-edit">
                    <span>Edit</span>
                </LocalizedStrict>
            </MenuItem>,
            <MenuItem
                key={`delete-${row.original.id}`}
                onClick={() => {
                    closeMenu()
                }}
            >
                <LocalizedStrict id="company-organization-users-table-button-delete">
                    <span>Delete</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [],
    )

    return (
        <>
            <LocalizedStrict id="company-organization-users-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_USERS}
                    title="Users"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="company-organization-users-button-create">
                            <Button
                                variant="contained"
                                onClick={() => {
                                    navigate(COMPANY_ADMIN_ORGANIZATION_USERS_CREATE_ROUTE)
                                }}
                            >
                                Create
                            </Button>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-admin-nav-item-organization">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-admin-nav-item-users">
                            <Breadcrumb>Users</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={renderRowActionMenuItems}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <SelectUserGroupDropdown userGroupId={userGroupId} setUserGroupId={setUserGroupId} />
                    </StandardTableFilter>
                )}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
            />

            {user && (
                <UserDetailsDialog
                    userId={user.id}
                    open={showDetailsModal}
                    userType="user"
                    onClose={() => setShowDetailsModal(false)}
                />
            )}
        </>
    )
}
